import type { ReactElement } from "react";
import { Link } from "@tanstack/react-router";
import { FiLoader } from "react-icons/fi";
import type { LibraryItem } from "../lib/useLibrary";
import { isContentProcessing } from "../lib/useLibrary";
import { formatDate, getItemLocation, getItemTitle } from "./libraryShared";

function ItemCard({ item }: { item: LibraryItem }): ReactElement {
	const processing = isContentProcessing(item);
	const status = item.contents?.status ?? null;
	const location = getItemLocation(item);
	const className = location
		? "border-line bg-surface hover:border-line-strong block cursor-pointer rounded-lg border px-3 py-2 shadow-sm transition-colors"
		: "border-line bg-surface block rounded-lg border px-3 py-2 shadow-sm";
	const content = (
		<div className="flex items-center justify-between gap-2">
			<span className="text-fg-secondary min-w-0 truncate text-sm">
				{getItemTitle(item)}
			</span>
			<div className="flex shrink-0 items-center gap-2">
				{processing && (
					<FiLoader className="text-accent h-3.5 w-3.5 animate-spin" />
				)}
				<span className="text-fg-ghost text-[11px]">
					{formatDate(item.added_at)}
				</span>
			</div>
		</div>
	);

	if (location) {
		return (
			<Link
				{...location}
				className={className}
				data-testid="recent-item"
				data-content-id={item.content_id}
				{...(status ? { "data-status": status } : {})}
			>
				{content}
			</Link>
		);
	}

	return (
		<div
			className={className}
			data-testid="recent-item"
			data-content-id={item.content_id}
			{...(status ? { "data-status": status } : {})}
		>
			{content}
		</div>
	);
}

function SkeletonItems(): ReactElement {
	return (
		<div className="space-y-1.5">
			{[0, 1, 2, 3].map((i) => (
				<div
					key={i}
					className="border-line rounded-lg border px-3 py-2"
				>
					<div className="flex items-center justify-between">
						<div className="bg-skeleton h-4 w-48 animate-pulse rounded" />
						<div className="bg-skeleton h-3 w-12 animate-pulse rounded" />
					</div>
				</div>
			))}
		</div>
	);
}

interface RecentItemsListProps {
	items: LibraryItem[] | undefined;
	isLoading: boolean;
}

export function RecentItemsList({
	items,
	isLoading,
}: RecentItemsListProps): ReactElement {
	if (isLoading) {
		return <SkeletonItems />;
	}

	if (!items || items.length === 0) {
		return (
			<p className="text-fg-ghost py-8 text-center text-sm font-light">
				No items yet. Paste a URL above to get started.
			</p>
		);
	}

	return (
		<div className="space-y-1.5">
			{items.map((item) => (
				<ItemCard key={item.id} item={item} />
			))}
		</div>
	);
}
